import React, { useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Menu } from 'primereact/menu';
import { MenuItem } from 'primereact/menuitem';
import { Toast } from 'primereact/toast';
import { RolesSummaryDTO } from '../../models/user-role/role-model';
import AppLoading from '../app-loading/app-loading';

interface NewRoleTableProps {
    data: RolesSummaryDTO[];
    loading: boolean;
    onEdit: (role: RolesSummaryDTO) => void;
    onDelete: (role: RolesSummaryDTO) => void;
    toast: React.RefObject<Toast>;
}

const NewRoleTable: React.FC<NewRoleTableProps> = ({ data, loading, onEdit, onDelete, toast }) => {
    const menu = useRef<Menu>(null);
    const actionRowData = useRef<RolesSummaryDTO | null>(null);

    const confirmDelete = (rowData: RolesSummaryDTO) => {
        confirmDialog({
            message: 'Are you sure you want to delete this role "' + rowData.name + '"?',
            header: 'Confirm',
            icon: 'pi pi-exclamation-triangle',
            accept: () => onDelete(rowData)
        });
    };

    const menuItems: MenuItem[] = [
        {
            label: 'Edit',
            icon: 'pi pi-pencil',
            command: () => {
                if (actionRowData.current) {
                    onEdit(actionRowData.current);
                }
            }
        },
        {
            label: 'Delete',
            icon: 'pi pi-trash',
            className: 'text-red-500',
            command: () => {
                if (actionRowData.current) {
                    confirmDelete(actionRowData.current);
                }
            }
        }
    ];

    const actionTemplate = (rowData: RolesSummaryDTO) => {
        return (
            <div className="flex justify-content-center">
                <Button
                    icon="pi pi-ellipsis-v"
                    className="p-button-rounded p-button-text p-button-secondary"
                    onClick={(e) => {
                        actionRowData.current = rowData;
                        menu.current?.toggle(e);
                    }}
                    tooltip="Options"
                />
            </div>
        );
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return "";
        const dateValue = dateString.endsWith('Z') || dateString.includes('+') ? dateString : dateString + 'Z';
        const date = new Date(dateValue);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ', ' +
            date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    };

    return (
        <div className="role-table-card relative">
            <ConfirmDialog />
            <Menu model={menuItems} popup ref={menu} />

            {loading && <AppLoading />}

            <DataTable
                value={data}
                paginator
                rows={10}
                rowsPerPageOptions={[5, 10, 25, 50]}
                tableStyle={{ minWidth: '50rem' }}
                stripedRows
                paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                currentPageReportTemplate="{first} to {last} of {totalRecords} records"
                emptyMessage="No roles found.">

                <Column field="name" header="Name" sortable style={{ width: '15%' }}></Column>
                <Column field="description" header="Description" sortable style={{ width: '20%' }}></Column>
                <Column field="modifiedByUserName" header="Modified By" sortable style={{ width: '15%' }}></Column>
                <Column field="modifiedDate" header="Modified Date" sortable body={(rowData) => formatDate(rowData.modifiedDate)} style={{ width: '15%' }}></Column>
                <Column body={actionTemplate} header="Actions" alignHeader="center" style={{ width: '5%', textAlign: 'center' }}></Column>
            </DataTable>
        </div>
    );
};

export default NewRoleTable;
